import React, { useState, useRef, useEffect } from "react";
import { Modal, Button, Form, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import { useForm, Controller } from "react-hook-form";
import UserService from "services/UserService";
import { AuthHelper } from "utils/AuthHelper";
import {
  PLEASE_ENTER_VALID_CONFIRMATION_CODE,
  PLEASE_ENTER_PASSWORD,
  PLEASE_ENTER_CONFIRM_PASSWORD,
  PASSWORDS_DONT_MATCH,
  PASSWORD_RESET_SUCCESSFUL,
  INVALID_OTP,
  PLEASE_TRY_AGAIN,
  PASSWORD_RESET_CODE_TEXT,
  PLEASE_LOGIN_WITH_NEW_PASSWORD,
  LABEL_ENTER_CONFIRMATION_CODE,
  LABEL_ENTER_NEW_PASSWORD,
  LABEL_ENTER_CONFIRM_PASSWORD,
} from "configurations/MessageConstants";
import "styles/PasswordPopup.css";

const ResetPasswordPopup = ({ show, onResetPasswordClose, resetEmail }) => {
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const inputRef = useRef(null);

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    watch,
    trigger,
  } = useForm({
    defaultValues: {
      confirmationCode: "",
      newPassword: "",
      confirmPassword: "",
    },
    mode: "onChange",
  });

  const newPassword = watch("newPassword");
  const confirmPassword = watch("confirmPassword");

  useEffect(() => {
    if (show && inputRef.current) {
      inputRef.current.focus();
    }
  }, [show]);

  useEffect(() => {
    if (confirmPassword) {
      trigger("confirmPassword");
    }
  }, [newPassword, trigger, confirmPassword]);

  const onSubmit = async (data) => {
    try {
      await UserService.resetPassword(
        resetEmail,
        data.newPassword,
        data.confirmationCode
      );
      handlePasswordResetSuccess();
    } catch (error) {
      console.error(INVALID_OTP, error);
      toast.error(
        <div>
          {INVALID_OTP}
          <br />
          {PLEASE_TRY_AGAIN}
        </div>
      );
    }
  };

  const handleClose = () => {
    reset();
    setShowNewPassword(false);
    setShowConfirmPassword(false);
    onResetPasswordClose();
  };

  const handlePasswordResetSuccess = () => {
    toast.success(
      <div>
        {PASSWORD_RESET_SUCCESSFUL}
        <br />
        {PLEASE_LOGIN_WITH_NEW_PASSWORD}
      </div>
    );
    handleClose();
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      className="password-modal"
      centered
      aria-labelledby="reset-password-popup"
    >
      <Modal.Header closeButton className="border-0">
        <Modal.Title id="reset-password-popup">Reset Password</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="mb-3 text-muted">
          {PASSWORD_RESET_CODE_TEXT}: {resetEmail}
        </div>

        <Form noValidate onSubmit={handleSubmit(onSubmit)}>
          <Form.Group className="mb-4">
            <Controller
              name="confirmationCode"
              control={control}
              rules={{
                required: PLEASE_ENTER_VALID_CONFIRMATION_CODE,
                validate: (value) => {
                  if (!/^\d{6}$/.test(value)) {
                    return PLEASE_ENTER_VALID_CONFIRMATION_CODE;
                  }
                  return true;
                },
              }}
              render={({ field }) => (
                <Form.Control
                  {...field}
                  ref={inputRef}
                  type="text"
                  placeholder={LABEL_ENTER_CONFIRMATION_CODE}
                  className="text-center input-underline letter-spacing-wide"
                  maxLength={6}
                  disabled={isSubmitting}
                  isInvalid={!!errors.confirmationCode}
                  onChange={(e) => {
                    const numericValue = e.target.value.replace(/\D/g, "");
                    field.onChange(numericValue);
                  }}
                  style={{
                    caretColor:
                      field.value.length >= 6 ? "transparent" : "auto",
                  }}
                  autoComplete="off"
                />
              )}
            />
            <Form.Control.Feedback type="invalid">
              {errors.confirmationCode?.message}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-4">
            <Form.Label>New Password</Form.Label>
            <div className="position-relative">
              <Controller
                name="newPassword"
                control={control}
                rules={{
                  required: PLEASE_ENTER_PASSWORD,
                  validate: AuthHelper.isValidPassword,
                }}
                render={({ field }) => (
                  <Form.Control
                    {...field}
                    type={showNewPassword ? "text" : "password"}
                    placeholder={LABEL_ENTER_NEW_PASSWORD}
                    className="input-underline"
                    disabled={isSubmitting}
                    isInvalid={!!errors.newPassword}
                    autoComplete="new-password"
                    maxLength={50}
                  />
                )}
              />
              <Button
                variant="link"
                className="password-toggle-button"
                onClick={() => setShowNewPassword(!showNewPassword)}
                aria-label={showNewPassword ? "Hide password" : "Show password"}
              >
                <i
                  className={`fa fa-eye${showNewPassword ? "" : "-slash"}`}
                  aria-hidden="true"
                ></i>
              </Button>
              <Form.Control.Feedback type="invalid">
                {errors.newPassword?.message}
              </Form.Control.Feedback>
            </div>
          </Form.Group>

          <Form.Group className="mb-4">
            <Form.Label>Confirm Password</Form.Label>
            <div className="position-relative">
              <Controller
                name="confirmPassword"
                control={control}
                rules={{
                  required: PLEASE_ENTER_CONFIRM_PASSWORD,
                  validate: {
                    notEmpty: (value) =>
                      value.trim() !== "" || PLEASE_ENTER_CONFIRM_PASSWORD,
                    matchesPassword: (value) =>
                      value === newPassword || PASSWORDS_DONT_MATCH,
                  },
                }}
                render={({ field }) => (
                  <Form.Control
                    {...field}
                    type={showConfirmPassword ? "text" : "password"}
                    placeholder={LABEL_ENTER_CONFIRM_PASSWORD}
                    className="input-underline"
                    disabled={isSubmitting}
                    isInvalid={!!errors.confirmPassword}
                    autoComplete="new-password"
                    maxLength={50}
                  />
                )}
              />
              <Button
                variant="link"
                className="password-toggle-button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                aria-label={
                  showConfirmPassword ? "Hide password" : "Show password"
                }
              >
                <i
                  className={`fa fa-eye${showConfirmPassword ? "" : "-slash"}`}
                  aria-hidden="true"
                ></i>
              </Button>
              <Form.Control.Feedback type="invalid">
                {errors.confirmPassword?.message}
              </Form.Control.Feedback>
            </div>
          </Form.Group>

          <div className="d-flex gap-2 justify-content-end">
            <Button variant="outline-secondary" onClick={handleClose}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  Resetting
                  <Spinner animation="border" size="sm" className="ms-2" />
                </>
              ) : (
                "Reset Password"
              )}
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default ResetPasswordPopup;
